import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { runDemoValidation, getDemoValidation } from "../lib/adminApi";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle, XCircle, MinusCircle } from "lucide-react";

const STATUS_META = {
  PASS: { label: "PASS", color: "#047857", bg: "#ECFDF5", border: "#047857", Icon: CheckCircle2 },
  WARNING: { label: "WARNING", color: "#B45309", bg: "#FFFBEB", border: "#B45309", Icon: AlertTriangle },
  FAIL: { label: "FAIL", color: "#B91C1C", bg: "#FEF2F2", border: "#B91C1C", Icon: XCircle },
  NOT_CONFIGURED: { label: "NOT CONFIGURED", color: "#6B7280", bg: "#F3F4F6", border: "#9CA3AF", Icon: MinusCircle },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.NOT_CONFIGURED;
  const { Icon } = m;
  return (
    <span
      data-testid={`validation-status-${status}`}
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-sm px-1.5 py-0.5 border"
      style={{ color: m.color, background: m.bg, borderColor: m.border + "55" }}
    >
      <Icon className="w-3 h-3" />
      {m.label}
    </span>
  );
}

function ExpectedActual({ expected, actual, match }) {
  return (
    <div className="leading-tight">
      <div className="text-[12px] text-[#0F172A]">{actual || "—"}</div>
      {expected && expected !== actual && (
        <div className={`text-[10px] ${match ? "text-[#6B7280]" : "text-[#B91C1C]"}`}>
          exp: {expected}
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, color, testid }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="text-2xl font-bold leading-none" style={{ color, fontFamily: "Chivo" }}>
        {value}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mt-1">
        {label}
      </span>
    </div>
  );
}

export default function DemoProfileValidationPage() {
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getDemoValidation();
      setRun(data && data.last_run ? data : null);
    } catch {
      setRun(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRun = async () => {
    setValidating(true);
    try {
      const data = await runDemoValidation();
      setRun(data);
      const f = data?.summary?.fail || 0;
      const w = data?.summary?.warning || 0;
      if (f) toast.error(`Validation complete — ${f} profile(s) FAILED`);
      else if (w) toast.warning(`Validation complete — ${w} warning(s)`);
      else toast.success("Validation complete — all profiles healthy");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed to run");
    } finally {
      setValidating(false);
    }
  };

  const summary = run?.summary;
  const results = run?.results || [];
  const lastRun = run?.last_run ? new Date(run.last_run).toLocaleString() : "Never";

  const norm = (s) => (s || "").trim().toLowerCase().replace("sap ", "");

  return (
    <div data-testid="demo-validation-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-1 gap-3 flex-wrap">
        <div>
          <button
            onClick={() => navigate("/admin/demo-profiles")}
            data-testid="validation-back-link"
            className="inline-flex items-center gap-1 text-xs text-[#6B7280] hover:text-[#025979] mb-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Demo Profiles
          </button>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }} data-testid="validation-title">
            Demo Profile Validation
          </h1>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            Regression check for SmartEvaluate. Each active demo profile is
            re-processed through the live scoring engine and compared against
            its expected recommendation. Run this after any scoring, capability,
            trigger or recommendation change.
          </p>
        </div>
        <button
          onClick={handleRun}
          disabled={validating}
          data-testid="run-validation-btn"
          className="shrink-0 inline-flex items-center gap-2 text-xs font-semibold text-white bg-[#025979] hover:bg-[#014459] disabled:opacity-60 rounded-sm px-4 py-2.5 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${validating ? "animate-spin" : ""}`} />
          {validating ? "Running…" : "Run Validation"}
        </button>
      </div>

      {/* Summary card */}
      <div
        className="mt-5 rounded-sm border border-[#E5E7EB] bg-white px-6 py-5 flex items-center gap-10 flex-wrap"
        data-testid="validation-summary-card"
      >
        <SummaryStat label="Demo Profiles" value={summary?.total ?? "—"} color="#111827" testid="summary-total" />
        <SummaryStat label="Pass" value={summary?.pass ?? "—"} color="#047857" testid="summary-pass" />
        <SummaryStat label="Warning" value={summary?.warning ?? "—"} color="#B45309" testid="summary-warning" />
        <SummaryStat label="Fail" value={summary?.fail ?? "—"} color="#B91C1C" testid="summary-fail" />
        {!!summary?.not_configured && (
          <SummaryStat label="Not Configured" value={summary.not_configured} color="#6B7280" testid="summary-notconfigured" />
        )}
        <div className="flex flex-col ml-auto">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Last Run</span>
          <span className="text-xs text-[#0F172A] mt-1" data-testid="validation-last-run">{lastRun}</span>
        </div>
      </div>

      {/* Table */}
      <div className="mt-5 border border-[#E5E7EB] rounded-sm overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="validation-table">
          <thead>
            <tr className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Profile</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Solution</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Deployment</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium">Complexity</th>
              <th className="text-left px-4 py-3 text-[#6B7280] font-medium w-36">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-[#9CA3AF]">Loading…</td></tr>
            ) : results.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-xs text-[#9CA3AF]" data-testid="validation-empty">
                  No validation run yet. Click <strong>Run Validation</strong> to check all demo profiles.
                </td>
              </tr>
            ) : (
              results.map((r) => (
                <tr key={r.profile_id} className="border-b border-[#F1F5F9] last:border-0" data-testid={`validation-row-${r.profile_id}`}>
                  <td className="px-4 py-3">
                    <div className="text-[13px] font-medium text-[#111827]">{r.profile_name}</div>
                    <div className="text-[10px] text-[#9CA3AF] capitalize">{r.scenario_type}</div>
                  </td>
                  <td className="px-4 py-3">
                    <ExpectedActual expected={r.expected_solution} actual={r.actual_solution} match={norm(r.expected_solution) === norm(r.actual_solution)} />
                  </td>
                  <td className="px-4 py-3">
                    <ExpectedActual expected={r.expected_deployment} actual={r.actual_deployment} match />
                  </td>
                  <td className="px-4 py-3">
                    <ExpectedActual expected={r.expected_complexity} actual={r.actual_complexity} match />
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-[#9CA3AF] mt-3 max-w-3xl">
        <strong>PASS</strong> = solution, deployment &amp; complexity all match the
        expected baseline. <strong>WARNING</strong> = solution matches but
        deployment or complexity drifted. <strong>FAIL</strong> = the
        recommended solution no longer matches the expected outcome.
      </p>
    </div>
  );
}
